import { SiteHeader } from "@/components/site-header"

export default function SecurityPendingPage() {
  return (
    <>
      <SiteHeader title="Seguridad — Pendientes para Producción" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="w-full space-y-10">

          <p className="text-sm text-muted-foreground">
            Ítems identificados en el análisis de seguridad previo al deploy a producción.
            Los marcados como <span className="font-semibold text-destructive">Alto</span> deben resolverse antes del go-live.
            Los <span className="font-semibold text-yellow-600 dark:text-yellow-400">Medios</span> y{" "}
            <span className="font-semibold text-muted-foreground">Bajos</span> pueden postergarse pero conviene documentarlos.
          </p>

          {/* ─── ALTO ──────────────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-destructive" />
              Alto
            </h2>
            <div className="space-y-4">

              <div className="border rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-semibold">Puerto 3001 no debe estar expuesto al host en Docker Compose</h3>
                <p className="text-sm text-muted-foreground">
                  En producción, el servicio <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">api</code> en{" "}
                  <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">docker-compose.yml</code> no debe publicar el puerto 3001
                  al host con <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">ports: - "3001:3001"</code>.
                  Solo nginx debe acceder a él por la red interna de Docker. Si el puerto está expuesto, el rate-limit
                  de login puede bypassearse enviando el header{" "}
                  <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">X-Forwarded-For</code> con IPs rotadas.
                </p>
                <div className="bg-muted rounded-md p-3 text-xs font-mono space-y-1">
                  <p className="text-destructive line-through"># MAL — expone el puerto al host</p>
                  <p className="text-destructive line-through">ports:</p>
                  <p className="text-destructive line-through">{"  "}- "3001:3001"</p>
                  <p className="mt-2 text-green-600 dark:text-green-400"># BIEN — solo accesible desde la red interna</p>
                  <p className="text-green-600 dark:text-green-400">expose:</p>
                  <p className="text-green-600 dark:text-green-400">{"  "}- "3001"</p>
                </div>
              </div>

            </div>
          </section>

          {/* ─── MEDIO ─────────────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-yellow-500" />
              Medio
            </h2>
            <div className="space-y-4">

              <div className="border rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-semibold">API Key embebida en el binario de la app mobile</h3>
                <p className="text-sm text-muted-foreground">
                  <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">EXPO_PUBLIC_API_KEY</code> queda
                  dentro del bundle JS del APK/IPA y puede extraerse con herramientas de ingeniería inversa.
                  Es una limitación inherente de las apps React Native. El impacto real es limitado porque la clave sola
                  no permite operar sin credenciales válidas (JWT + sesión en DB), pero conviene tomar las siguientes medidas:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 pl-1">
                  <li>
                    Usar una <strong>clave distinta</strong> para la app mobile y para la web. Así se puede revocar
                    una sin afectar la otra. Basta con agregar un segundo env var{" "}
                    <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">API_KEY_MOBILE</code> y validarlo
                    en <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">apiKey.ts</code>.
                  </li>
                  <li>
                    Rotar la clave ante cualquier sospecha de compromiso (nueva versión del APK + nuevo env var en el servidor).
                  </li>
                </ul>
              </div>

              <div className="border rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-semibold">JWT almacenado en <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">localStorage</code> (web)</h3>
                <p className="text-sm text-muted-foreground">
                  El token JWT de la web vive en <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">localStorage</code>,
                  accesible a cualquier script JavaScript. Si existiera una vulnerabilidad XSS, el token podría
                  robarse. El CSRF ya está mitigado. Para reducir la superficie:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 pl-1">
                  <li>
                    Verificar que <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">secureHeaders()</code> en Hono
                    incluya un <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">Content-Security-Policy</code> restrictivo
                    que bloquee scripts inline y fuentes no confiables.
                  </li>
                  <li>
                    No incorporar scripts de terceros (analytics, chat, etc.) sin auditar su cadena de suministro.
                  </li>
                  <li>
                    Alternativa a largo plazo: migrar a <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">httpOnly cookie</code>{" "}
                    para el token, lo que elimina el riesgo de robo por XSS. Requiere refactor del auth store y del servidor.
                  </li>
                </ul>
              </div>

            </div>
          </section>

          {/* ─── BAJO ──────────────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground" />
              Bajo
            </h2>
            <div className="space-y-4">

              <div className="border rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-semibold">Rate limit in-memory se resetea al reiniciar el proceso</h3>
                <p className="text-sm text-muted-foreground">
                  El contador de intentos de login vive en un{" "}
                  <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">Map</code> en memoria.
                  Un deploy, crash o reinicio del contenedor limpia los contadores. En el caso de uso actual
                  (backoffice logístico, no sistema bancario) es aceptable. Si se necesita mayor robustez:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 pl-1">
                  <li>Persistir los contadores en Redis con TTL, o</li>
                  <li>
                    Guardarlos en la tabla <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">sessions</code> de
                    PostgreSQL con una columna <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">login_attempts</code>.
                  </li>
                </ul>
              </div>

              <div className="border rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-semibold">
                  <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">GET /trips/:id</code> y{" "}
                  <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">GET /shipments/:id</code> sin verificación de ownership
                </h3>
                <p className="text-sm text-muted-foreground">
                  Cualquier usuario autenticado (incluyendo drivers) puede obtener el detalle de cualquier viaje o
                  envío conociendo su UUID. El riesgo es bajo porque los UUIDs v7 son impredecibles y no enumerables,
                  pero en un modelo de datos multiempresa más estricto correspondería filtrar por ownership.
                  Por ahora la mitigación natural es la baja probabilidad de adivinanza + el doble factor JWT+sesión.
                </p>
              </div>

            </div>
          </section>

          {/* ─── RESUELTOS ─────────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              Resueltos en el último hardening
            </h2>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Ítem</th>
                    <th className="text-left px-4 py-2 font-medium">Archivo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-muted-foreground">
                  <tr>
                    <td className="px-4 py-2"><code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/status</code> exponía detalles de DB en el error</td>
                    <td className="px-4 py-2 font-mono text-xs">apps/api/src/index.ts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Driver podía crear shipments (<code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">POST /shipments</code>)</td>
                    <td className="px-4 py-2 font-mono text-xs">apps/api/src/routes/shipments.ts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Driver podía modificar cualquier campo de cualquier shipment (<code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">PATCH /shipments/:id</code>)</td>
                    <td className="px-4 py-2 font-mono text-xs">apps/api/src/routes/shipments.ts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Driver podía modificar cualquier trip (<code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">PATCH /trips/:id</code>)</td>
                    <td className="px-4 py-2 font-mono text-xs">apps/api/src/routes/trips.ts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2"><code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">console.log</code> con URL de la API en builds de producción mobile</td>
                    <td className="px-4 py-2 font-mono text-xs">apps/driver/lib/api.ts</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
